import React, { useMemo } from 'react';
import { View, Text, Platform, StyleSheet } from 'react-native';
import KpiModuleCard from '../charts/KpiModuleCard';
import TrendKpiCard from '../charts/TrendKpiCard';
import MiniDonutChart from '../charts/MiniDonutChart';
import { calcNetSpoilBalance, calcRegionalRatio, calcRecycledContentPercent } from '../kpiService';

interface Props {
  spoil:        any[];
  importedSoil: any[];
  recycled:     any[];
  regional:     any[];
  stretch?:     boolean;
}

function HorizontalBar({ label, value, max, color, unit }: { label: string; value: number; max: number; color: string; unit: string }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  if (Platform.OS !== 'web') {
    return (
      <View style={hStyles.row}>
        <Text style={hStyles.label}>{label}</Text>
        <Text style={hStyles.val}>{value.toLocaleString()} {unit}</Text>
      </View>
    );
  }
  return (
    <View style={hStyles.row}>
      <Text style={hStyles.label}>{label}</Text>
      <View style={hStyles.track}>
        <View style={[hStyles.fill, { width: `${pct}%` as any, backgroundColor: color }]} />
      </View>
      <Text style={hStyles.val}>{value.toLocaleString()} {unit}</Text>
    </View>
  );
}

const hStyles = StyleSheet.create({
  row:   { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 },
  label: { fontSize: 10, color: '#666', width: 120 },
  track: { flex: 1, height: 10, backgroundColor: '#EEEEEE', borderRadius: 5, overflow: 'hidden' },
  fill:  { height: 10, borderRadius: 5 },
  val:   { fontSize: 10, color: '#444', fontWeight: '700', width: 70, textAlign: 'right' },
});

export default function MaterialModule({ spoil, importedSoil, recycled, regional, stretch }: Props) {
  const balance    = useMemo(() => calcNetSpoilBalance(spoil, importedSoil), [spoil, importedSoil]);
  const regRatio   = useMemo(() => calcRegionalRatio(regional, recycled), [regional, recycled]);
  const recycledPct = useMemo(() => calcRecycledContentPercent(recycled), [recycled]);

  const maxVal = Math.max(balance.generated, balance.imported, balance.reused, balance.disposed, 1);

  const regional_ = Array.isArray(regional) ? regional : [];
  const recycled_ = Array.isArray(recycled) ? recycled : [];
  const regionalVal = regional_.reduce((s: number, i: any) => s + (parseFloat(i.MaterialValue) || 0), 0);
  const recycledVal = recycled_.reduce((s: number, i: any) => s + (parseFloat(i.MaterialValue) || 0), 0);

  return (
    <KpiModuleCard title="Material Circularity KPIs" accentColor="#6A1B9A" stretch={stretch}>
      <View style={styles.row}>
        {/* Net Spoil Balance */}
        <View style={styles.block}>
          <Text style={styles.blockLabel}>NET SPOIL BALANCE</Text>
          <HorizontalBar label="Spoil Generated"      value={balance.generated} max={maxVal} color="#757575" unit={balance.unit} />
          <HorizontalBar label="↳ Reused (on/off-site)" value={balance.reused}   max={maxVal} color="#388E3C" unit={balance.unit} />
          <HorizontalBar label="↳ Disposed / Landfill"  value={balance.disposed} max={maxVal} color="#C62828" unit={balance.unit} />
          <HorizontalBar label="Soil Imported"          value={balance.imported}  max={maxVal} color="#1565C0" unit={balance.unit} />
          <View style={styles.netRow}>
            <Text style={styles.netLabel}>NET BALANCE</Text>
            <Text style={[styles.netValue, { color: balance.net > 0 ? '#C62828' : balance.net < 0 ? '#2E7D32' : '#555' }]}>
              {balance.net === 0
                ? 'Balanced'
                : `${balance.net > 0 ? '+' : ''}${balance.net.toLocaleString()} ${balance.unit} ${balance.net > 0 ? 'export' : 'import'}`}
            </Text>
          </View>
        </View>

        {/* Regional Material Ratio */}
        <View style={styles.donutBlock}>
          <Text style={styles.blockLabel}>REGIONAL MATERIAL RATIO</Text>
          <Text style={styles.absValue}>
            {regionalVal >= 1000000
              ? `${(regionalVal / 1000000).toFixed(1)}M`
              : regionalVal >= 1000
              ? `${(regionalVal / 1000).toFixed(1)}K`
              : regionalVal.toLocaleString()}
          </Text>
          <Text style={styles.absLabel}>Total Regional Value</Text>
          <MiniDonutChart
            segments={[
              { label: 'Regional',     value: regionalVal, color: '#43A047' },
              { label: 'Non-Regional', value: recycledVal, color: '#BDBDBD' },
            ]}
            size={90}
            centerValue={`${regRatio}%`}
            centerLabel="Regional"
          />
        </View>

        {/* Recycled Content */}
        <TrendKpiCard
          label="Recycled Content Percentage"
          value={`${recycledPct}%`}
          accent="#6A1B9A"
          subtitle="of total material value"
        />
      </View>
    </KpiModuleCard>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  block: {
    flex: 2,
    minWidth: 200,
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    padding: 12,
    marginVertical: 4,
  },
  donutBlock: {
    flex: 1,
    minWidth: 150,
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    marginVertical: 4,
  },
  blockLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: '#777',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 10,
  },
  netRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#E0E0E0',
    paddingTop: 8,
  },
  netLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: '#555',
    textTransform: 'uppercase',
  },
  netValue: {
    fontSize: 13,
    fontWeight: '800',
  },
  absValue: {
    fontSize: 24,
    fontWeight: '900',
    color: '#111',
    textAlign: 'center',
    marginTop: 4,
  },
  absLabel: {
    fontSize: 9,
    color: '#AAA',
    textAlign: 'center',
    marginBottom: 8,
  },
});
